import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MapPin, Navigation, Clock, Package } from 'lucide-react';
import { toast } from 'react-toastify';
import { dispatchAPI } from '../../../config/dispatch-api';
import './RouteOverview.css';
import { getMapboxToken, formatDateOnly, getBatchDirections } from '../../../utils/utils';
import { resolveMapUrl, openExternalUrl } from '../../../utils/mapCoordinates';
import { formatPriorityBadgeLabel } from '../../../utils/priorityDelivery';
import { getOrderTypeBadge } from '../../../utils/orderConfigHelpers';

const mapboxgl = window.mapboxgl;

const DEFAULT_DEPOT = [51.4116783, 25.2441101]; // [lon, lat] Ecatcho store

const RouteOverview = () => {
  const getDepotLabel = () => {
    try {
      const adminData = localStorage.getItem('adminData');
      if (adminData) {
        const parsed = JSON.parse(adminData);
        const name = parsed?.currentStore?.storeName || parsed?.storeName;
        if (name) return name;
      }
    } catch (e) {
      console.error('Error parsing adminData for store name:', e);
    }
    return 'Ecatcho Base';
  };
  const DEPOT_LABEL = getDepotLabel();

  const [searchParams] = useSearchParams();
  const driver = searchParams.get('driver');
  const slot = searchParams.get('slot');
  const date = searchParams.get('date');
  const routeId = searchParams.get('routeId');

  const [routeData, setRouteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const mapContainer = useRef(null);
  const map = useRef(null);

  useEffect(() => {
    const fetchRouteData = async () => {
      try {
        setLoading(true);

        // Validate required parameters
        if (!driver) {
          setError('Driver parameter is missing');
          setLoading(false);
          return;
        }

        if (!routeId) {
          setError('Route ID is missing');
          setLoading(false);
          return;
        }

        const result = await dispatchAPI.getRouteOverview(driver, date, slot, routeId);

        if (result.success && result.data) {
          setRouteData(result.data);
        } else {
          setError(result.msg || 'Route not found');
        }
      } catch (err) {
        console.error('Error fetching route:', err);
        setError('Failed to load route data');
      } finally {
        setLoading(false);
      }
    };

    // Trigger fetch with proper validation
    if (driver && routeId) {
      fetchRouteData();
    } else {
      setError('Missing required parameters');
      setLoading(false);
    }
  }, [driver, routeId]);

  // Initialize Map
  useEffect(() => {
    if (!routeData || !mapContainer.current || map.current) return;

    // Use storeCoordinates from API response or fallback to default
    const depot = routeData.storeCoordinates || DEFAULT_DEPOT;

    const token = getMapboxToken();
    if (!token || !mapboxgl) return;
    mapboxgl.accessToken = token;

    try {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: depot,
        zoom: 11
      });

      map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

      map.current.on('load', () => {
        drawRoute();
      });
    } catch (err) {
      console.error('Map initialization error:', err);
    }

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [routeData]);

  const drawRoute = async () => {
    if (!map.current || !routeData || !routeData.plan || !routeData.plan.stops) return;

    const stops = routeData.plan.stops.filter(s => s.coord && s.coord.lat && s.coord.lon);
    if (stops.length === 0) return;

    // Get the driver's color from the plan, default to orange
    const routeColor = routeData.plan.color || '#FE8403';

    // Use storeCoordinates from API response or fallback to default
    const depot = routeData.storeCoordinates || DEFAULT_DEPOT;

    // Add Depot Marker 
    const startTime = (routeData.slot ? routeData.slot.split('-')[0].trim() : 'N/A');

    new mapboxgl.Marker({ color: '#3b82f6', scale: 1.0 })
      .setLngLat(depot)
      .setPopup(
        new mapboxgl.Popup().setHTML(`
          <div style="padding: 8px; text-align: center;">
            <h3 style="margin: 0 0 4px 0; color: #FE8403; font-size: 14px; font-weight: 600;">🏢 ${DEPOT_LABEL}</h3>
            <p style="margin: 0; color: #666; font-size: 12px;">Starting Point</p>
            <p style="margin: 4px 0 0 0; color: #666; font-size: 12px;">Start: ${startTime}</p>
          </div>
        `)
      )
      .addTo(map.current);

    // Add Stop Markers 
    const bounds = new mapboxgl.LngLatBounds(depot, depot);

    stops.forEach((stop, stopIndex) => {
      const coord = [stop.coord.lon, stop.coord.lat];
      bounds.extend(coord);

      // Create pill-shaped marker
      const markerElement = document.createElement("div");
      markerElement.style.cssText = `
        background: ${routeColor};
        color: white;
        padding: 6px 12px; 
        border-radius: 16px;
        font-size: 12px;
        font-weight: 500;
        border: none;
        box-shadow: 0 1px 3px rgba(0,0,0,0.2);
        cursor: pointer;
        text-align: center;
        min-width: 24px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        white-space: nowrap;
        gap: 6px;
      `;

      const stopCircle = document.createElement("div");
      stopCircle.style.cssText = `
        background: rgba(255,255,255,0.95);
        color: ${routeColor};
        width: 22px;
        height: 22px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 11px;
        font-weight: 700;
        flex-shrink: 0;
        border: 2px solid rgba(255,255,255,0.3);
      `;
      stopCircle.textContent = stopIndex + 1;

      const orderText = document.createElement("span");
      orderText.style.fontWeight = '600';
      orderText.textContent = stop.order || "?";

      markerElement.appendChild(stopCircle);
      markerElement.appendChild(orderText);

      // Create popup with stop details
      const popup = new mapboxgl.Popup({ offset: 25 }).setHTML(`
        <div style="padding: 12px; min-width: 220px;">
          <div style="margin-bottom: 8px; padding-bottom: 6px; border-bottom: 1px solid #e5e7eb;">
            <h4 style="margin: 0; font-size: 14px; font-weight: 600; color: #111827;">📦 Stop ${stopIndex + 1}</h4>
          </div>
          <div style="font-size: 12px; line-height: 1.5; color: #374151;">
            <p style="margin: 0 0 4px 0;"><strong>Order #:</strong> ${stop.order || 'N/A'}</p>
            ${formatPriorityBadgeLabel(stop.priority_del_time)
              ? `<p style="margin: 0 0 6px 0;"><span style="display:inline-block;background:#dc2626;color:#fff;padding:2px 8px;border-radius:999px;font-size:11px;font-weight:700;">${formatPriorityBadgeLabel(stop.priority_del_time)}</span></p>`
              : ''}
            <p style="margin: 0 0 4px 0;"><strong>Customer:</strong> ${stop.customer || 'N/A'}</p>
            <p style="margin: 0 0 4px 0;"><strong>Total:</strong> ${stop.total || 'N/A'} QR</p>
            <p style="margin: 0 0 4px 0;"><strong>ETA (arrival):</strong> ${stop.eta_arrival || 'N/A'}</p>
            <p style="margin: 0 0 4px 0;"><strong>Depart:</strong> ${stop.depart_time || 'N/A'}</p>
            ${stop.isDelivered ? '<p style="margin: 4px 0; color: #22c55e; font-weight: 600;">✅ Delivered</p>' : ''}
          </div>
          <a href="https://maps.google.com/?q=${stop.coord.lat},${stop.coord.lon}" target="_blank" 
             style="display: inline-block; margin-top: 8px; padding: 6px 12px; background: #FE8403; color: white; text-decoration: none; border-radius: 6px; font-size: 12px; font-weight: 500;">
            📍 Open in Maps
          </a>
        </div>
      `);

      new mapboxgl.Marker({ element: markerElement })
        .setLngLat(coord)
        .setPopup(popup)
        .addTo(map.current);
    });

    // Fit bounds
    map.current.fitBounds(bounds, { padding: 50 });

    // Draw route line from DEPOT -> Stop 1 -> Stop 2 -> ... -> Stop N
    // using road-based routing via Mapbox Directions API
    const allPoints = [depot, ...stops.map(s => [s.coord.lon, s.coord.lat])];

    // Build road-based route from all legs in optimized batch call
    const buildRoute = async () => {
      const allCoords = await getBatchDirections(allPoints, mapboxgl.accessToken, 'driving');

      const geojson = {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: allCoords.length > 0 ? allCoords : allPoints
        }
      };

      if (map.current.getSource('route')) {
        map.current.getSource('route').setData(geojson);
      } else {
        map.current.addSource('route', {
          type: 'geojson',
          data: geojson
        });

        map.current.addLayer({
          id: 'route',
          type: 'line',
          source: 'route',
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': routeColor,
            'line-width': 5,
            'line-opacity': 0.9
          }
        });
      }
    };

    buildRoute();
  };

  const openInMaps = (lat, lon) => {
    const url = resolveMapUrl({ lat, lng: lon });
    if (url) openExternalUrl(url);
  };

  if (loading) {
    return (
      <div className="route-overview-loading">
        <div className="route-overview-spinner"></div>
        <p>Loading route details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="route-overview-error">
        <h3>Route Not Found</h3>
        <p>{error}</p>
        <p className="sub-text">Please fetch the route link from dispatch again.</p>
      </div>
    );
  }

  return (
    <div className="route-overview-container">
      <div className="route-overview-header">
        <div>
          <h2>Route Overview</h2>
          <div className="route-overview-meta">
            <span className="route-overview-badge driver">{routeData.driver}</span>
            <span className="route-overview-badge date">{formatDateOnly(routeData.date)}</span>
            <span className="route-overview-badge slot">{routeData.slot}</span>
          </div>
        </div>
        <div className="route-overview-stats">
          <div className="route-overview-stat">
            <Package size={16} />
            <span>{routeData.plan.stops?.length || 0} Stops</span>
          </div>
          <div className="route-overview-stat">
            <MapPin size={16} />
            <span>{routeData.plan.distance_km?.toFixed(1) || '0.0'} km</span>
          </div>
          <div className="route-overview-stat">
            <Clock size={16} />
            <span>{routeData.plan.total_hhmm}</span>
          </div>
        </div>
      </div>

      <div className="route-overview-content">
        <div className="route-overview-map-section">
          <div ref={mapContainer} className="route-overview-map-container" />
        </div>

        <div className="route-overview-timeline-section">
          <h3>Stops Timeline</h3>
          <div className="route-overview-timeline-list">
            {/* Depot Start */}
            <div className="route-overview-timeline-item start">
              <div className="route-overview-timeline-marker route-overview-start-marker"></div>
              <div className="route-overview-timeline-content">
                <h4>{DEPOT_LABEL}</h4>
                <span className="route-overview-time-badge">Start: {routeData.slot ? routeData.slot.split('-')[0].trim() : 'N/A'}</span>
              </div>
            </div>

            {/* Stops */}
            {routeData.plan.stops.map((stop, index) => (
              <div key={stop.order} className="route-overview-timeline-item">
                <div className="route-overview-timeline-marker" style={{ backgroundColor: routeData.plan.color || '#3b82f6', borderColor: 'white' }}>{index + 1}</div>
                <div className="route-overview-timeline-content">
                  <div className="route-overview-stop-header">
                    <h4>Stop {index + 1} {stop.isDelivered && <span style={{ color: '#22c55e', fontSize: '12px', fontWeight: 600, marginLeft: '8px' }}>✅ Delivered</span>}</h4>
                    <span className="route-overview-eta-badge">
                      Arr: {stop.eta_arrival || 'N/A'} <br />
                      Dep: {stop.depart_time || 'N/A'}
                    </span>
                  </div>
                  <p className="route-overview-order-id">
                    Order #{stop.order}
                    {getOrderTypeBadge(stop.type) && (
                      <span className={`dispatch-type-badge ${getOrderTypeBadge(stop.type).kind}`} style={{ marginLeft: 8 }}>
                        {getOrderTypeBadge(stop.type).label}
                      </span>
                    )}
                    {formatPriorityBadgeLabel(stop.priority_del_time) && (
                      <span className="dispatch-priority-badge" style={{ marginLeft: 8 }}>
                        {formatPriorityBadgeLabel(stop.priority_del_time)}
                      </span>
                    )}
                  </p>
                  <p className="route-overview-customer-name">{stop.customer}</p>
                  <p className="route-overview-address-text">{stop.coord ? `${stop.coord.lat.toFixed(4)}, ${stop.coord.lon.toFixed(4)}` : 'No location'}</p>

                  <button
                    className="route-overview-maps-btn"
                    onClick={() => openInMaps(stop.coord.lat, stop.coord.lon)}
                  >
                    <Navigation size={14} /> Open in Maps
                  </button>
                </div>
              </div>
            ))}

            {/* Base Return */}
            <div className="route-overview-timeline-item end">
              <div className="route-overview-timeline-marker route-overview-end-marker"></div>
              <div className="route-overview-timeline-content">
                <h4>Return to Base</h4>
                <span className="route-overview-time-badge">ETA: {routeData.plan.return_eta}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RouteOverview;